import { useCodatAuth } from "@codat/client-cookie-manager";
import { ErrorDialog, Skeleton } from "@codat/orchard-ui";
import { InviteValidation, validateInvite } from "api/invite";
import React, { PropsWithChildren, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import formatUrl from "utils/formatUrl";
import getParams from "utils/getParams";

import AxiosErrorDialog from "components/AxiosErrorDialog";
import Page from "components/Page";

export const HasValidInvite: React.FC<
  PropsWithChildren<{
    inviteId: string;
    validateUser: boolean;
    handleInviteCallback?: (
      inviteValidation: InviteValidation | undefined
    ) => void;
  }>
> = ({ inviteId, validateUser, handleInviteCallback, children }) => {
  const history = useHistory();
  const search = useLocation().search;
  const { userData, authType } = useCodatAuth();

  const [{ loading, error, data: inviteValidation }] = validateInvite(
    inviteId,
    validateUser
  );

  useEffect(() => {
    if (handleInviteCallback != undefined && !loading) {
      handleInviteCallback(inviteValidation);
    }
  }, [handleInviteCallback, loading, inviteValidation]);

  if (loading) {
    return (
      <Page>
        <Skeleton />
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <AxiosErrorDialog error={error} activity="validating invite" />
      </Page>
    );
  }

  if (inviteValidation && !inviteValidation.isValid) {
    if (inviteValidation.isInvalidUser) {
      history.push(
        formatUrl(
          "/invite/:inviteId/signin",
          {
            inviteId,
          },
          {
            errorMessage: inviteValidation.errorMessage,
          }
        )
      );
      return (
        <Page>
          <Skeleton />
        </Page>
      );
    } else {
      return (
        <ErrorDialog statusCode={400} message={inviteValidation.errorMessage} />
      );
    }
  }

  if (validateUser) {
    if (inviteValidation && !inviteValidation.userExists) {
      const params = getParams(search);
      const url =
        formatUrl("/invite/:inviteId/register", { inviteId }) + params;
      history.push(url);
      return (
        <Page>
          <Skeleton />
        </Page>
      );
    }

    const userIsLoggedIn = authType === "OIDC" && !userData?.expired;

    if (!userIsLoggedIn) {
      history.push(
        formatUrl("/invite/:inviteId/signin", {
          inviteId,
        })
      );
      return (
        <Page>
          <Skeleton />
        </Page>
      );
    }
  }

  return <>{children}</>;
};
